import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { categoryLabel } from "../lib/api";
import CompetitionCard from "../components/CompetitionCard";
import SEO from "../components/SEO";
import { publicCompetitionOperators } from "../lib/publicOperators";

const CATEGORY_LINKS = ["cars", "cash", "bikes", "tech", "watches", "gaming", "lifestyle", "other"];

const normalisedCategory = (competition) => {
  const category = String(competition?.category || "").trim().toLowerCase();
  if (["car", "vehicle", "vehicles"].includes(category)) return "cars";
  if (["bike", "motorbike", "motorcycle"].includes(category)) return "bikes";
  if (category === "watch") return "watches";
  return category;
};

const PAGE_CONFIG = {
  overview: {
    path: "/uk-competitions",
    title: "UK Competitions | Compare Odds, Prices & Competition Sites",
    description: "Compare UK competitions across cars, cash, bikes, tech and more. Explore live competitions, ticket prices, ticket caps, odds and UK competition operators.",
    overline: "Independent UK comparison",
    heading: "UK Competitions, Compared in One Place",
    intro: "Competition Checker brings public information from tracked UK competition sites into one consistent view. Compare ticket prices, trusted ticket caps, calculated odds, draw dates and operator details without treating a listing as an endorsement.",
  },
  cars: {
    path: "/uk-car-competitions",
    title: "UK Car Competitions | Compare Odds, Prices & Ticket Caps",
    description: "Compare live UK car competitions. Check ticket prices, ticket caps, calculated odds, cash alternatives, closing dates and competition operators before you enter.",
    overline: "Cars and vehicles",
    heading: "Compare UK Car Competitions",
    intro: "Compare current UK car competitions using the same evidence rules across operators. See the trusted ticket price, competition-wide ticket cap, calculated maximum-pool odds, closing date and any explicit cash alternative before visiting the official source.",
  },
};

export default function CompetitionSeoLanding({ variant = "overview" }) {
  const config = PAGE_CONFIG[variant] || PAGE_CONFIG.overview;
  const [competitions, setCompetitions] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/competitions", { params: { limit: 500 } }),
      api.get("/operators"),
    ])
      .then(([competitionResponse, operatorResponse]) => {
        if (!active) return;
        setCompetitions(Array.isArray(competitionResponse.data) ? competitionResponse.data : []);
        setOperators(Array.isArray(operatorResponse.data) ? operatorResponse.data : []);
      })
      .catch(() => {
        if (!active) return;
        setCompetitions([]);
        setOperators([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const publicOperators = useMemo(
    () => publicCompetitionOperators(operators),
    [operators],
  );
  const matchingCompetitions = useMemo(
    () => variant === "cars"
      ? competitions.filter((competition) => normalisedCategory(competition) === "cars")
      : competitions,
    [competitions, variant],
  );
  const visibleCompetitions = matchingCompetitions.slice(0, variant === "cars" ? 24 : 6);

  return (
    <div>
      <SEO
        title={config.title}
        exactTitle
        description={config.description}
        path={config.path}
        ogType="website"
      />

      <header className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-12">
        <div className="overline text-[var(--cc-gold)] mb-3">{config.overline}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight max-w-4xl">
          {config.heading}
        </h1>
        <p className="mt-5 max-w-3xl text-[var(--cc-text-muted)] leading-relaxed">
          {config.intro}
        </p>
        <nav className="mt-6 flex flex-wrap gap-x-5 gap-y-3 text-sm" aria-label="Competition landing page actions">
          <Link className="text-[var(--cc-gold)] hover:underline" to="/competitions">Browse all live competitions</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/best-odds-for-budget">Compare Budget Odds</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/operators">View tracked operators</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/how-we-score">Read the methodology</Link>
        </nav>
      </header>

      {variant === "overview" ? (
        <>
          <section className="border-y border-white/10 bg-white/[0.015]">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-2">
              <div>
                <h2 className="font-display text-2xl font-semibold">How Competition Checker works</h2>
                <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                  We preserve operator identity and public source evidence, then apply
                  consistent display and trust rules. Prices, dates, prize details and
                  ticket caps remain separate fields so a headline cannot silently fill
                  a missing fact.
                </p>
              </div>
              <div>
                <h2 className="font-display text-2xl font-semibold">How we calculate competition odds</h2>
                <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                  Maximum-pool odds use a trusted competition-wide ticket allocation.
                  They describe the supported denominator, not the final number sold and
                  never a guarantee of winning.
                </p>
              </div>
              <div>
                <h2 className="font-display text-2xl font-semibold">Why ticket price alone is not enough</h2>
                <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                  A low entry price can sit inside a very large pool. Comparing price with
                  the total allocation gives useful context that price by itself cannot provide.
                </p>
              </div>
              <div>
                <h2 className="font-display text-2xl font-semibold">What &quot;Odds unavailable&quot; means</h2>
                <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                  The competition can still be useful to compare, but the evidence does
                  not support a reliable denominator. We do not replace it with a per-user
                  limit or a guessed ticket cap.
                </p>
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
            <h2 className="font-display text-2xl font-semibold">Browse competitions by category</h2>
            <nav className="mt-5 flex flex-wrap gap-x-6 gap-y-3" aria-label="Competition categories">
              {CATEGORY_LINKS.map((category) => (
                <Link key={category} className="text-[var(--cc-gold)] hover:underline" to={`/competitions?category=${category}`}>
                  {categoryLabel(category)} competitions
                </Link>
              ))}
            </nav>
          </section>

          <section className="border-y border-white/10">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
              <h2 className="font-display text-2xl font-semibold">UK competition operators we track</h2>
              <p className="mt-3 text-sm text-[var(--cc-text-muted)] max-w-3xl">
                The directory keeps approved operator identities visible even during a temporary zero-record state.
              </p>
              <nav className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="Tracked UK competition operators">
                {publicOperators.map((operator) => (
                  <Link key={operator.id} className="text-[var(--cc-gold)] hover:underline" to={`/operators/${operator.slug}`}>
                    {operator.name}
                  </Link>
                ))}
              </nav>
            </div>
          </section>
        </>
      ) : (
        <section className="border-y border-white/10 bg-white/[0.015]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-2">
            <div>
              <h2 className="font-display text-2xl font-semibold">How car competition odds are compared</h2>
              <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                We calculate maximum-pool odds only from a trusted total allocation.
                Current sales may improve the live chance, but they do not change the
                published maximum-pool denominator.
              </p>
            </div>
            <div>
              <h2 className="font-display text-2xl font-semibold">Total ticket cap versus per-user limit</h2>
              <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                A per-person purchase limit controls how many entries one customer may
                buy. It is never treated as the total number of tickets in the draw.
              </p>
            </div>
            <div>
              <h2 className="font-display text-2xl font-semibold">Cash alternatives stay explicit</h2>
              <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                We show a cash alternative only when the source labels it as one. Included
                cash, finance support and package extras remain separate from the vehicle prize.
              </p>
            </div>
            <div>
              <h2 className="font-display text-2xl font-semibold">How listings are verified</h2>
              <p className="mt-4 text-[var(--cc-text-muted)] leading-relaxed">
                Public cards retain the exact operator identity, source URL, image evidence,
                trusted price, date and category required by that operator&apos;s publication contract.
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
          <div>
            <div className="overline text-[var(--cc-gold)] mb-2">Live comparisons</div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold">
              {variant === "cars" ? "Current UK car competitions" : "Current UK competitions"}
            </h2>
            <p className="mt-2 text-sm text-[var(--cc-text-muted)]">
              {loading ? "Loading current public records..." : `${matchingCompetitions.length} current public ${variant === "cars" ? "car competitions" : "competitions"}`}
            </p>
          </div>
          <Link className="text-sm text-[var(--cc-gold)] hover:underline" to={variant === "cars" ? "/competitions?category=cars" : "/competitions"}>
            Open the full catalogue
          </Link>
        </div>
        {!loading && visibleCompetitions.length === 0 ? (
          <p className="text-[var(--cc-text-muted)]">No current public competitions are available in this view.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleCompetitions.map((competition, index) => (
              <CompetitionCard key={competition.id} comp={competition} index={index} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
